/**
 * Offline-safe attendance capture queue.
 *
 * When a supervisor (PMC or Contractor) captures a worker photo in a
 * no-network area, the POST /api/attendance call can hang or fail. Instead
 * of losing the photo, we persist the payload to localStorage and let a
 * background flusher upload it the next time the browser regains network.
 *
 * Storage layout
 * --------------
 *   Key:   pmc.attendance.queue
 *   Value: JSON array of:
 *     {
 *       id: <client-uuid>,         // stable id for dedupe
 *       enqueued_at: ISO string,   // for "older than X" hints in UI
 *       attempts: int,             // increment on each failed flush
 *       payload: { ...POST body },
 *     }
 *
 * Each entry is dropped from the queue as soon as the server returns 2xx,
 * or — for terminal errors (e.g. duplicate capture, 4xx that cannot be
 * retried) — when the caller passes `dropOn4xx=true` to `flushQueue`.
 */

const KEY = "pmc.attendance.queue";

function read() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const v = JSON.parse(raw);
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
}

function write(list) {
  try {
    localStorage.setItem(KEY, JSON.stringify(list));
  } catch {
    /* quota — best effort */
  }
}

function newId() {
  // Lightweight UUID — sufficient for dedupe inside one device.
  return "att_" + Math.random().toString(36).slice(2) + "_" + Date.now().toString(36);
}

export function enqueueCapture(payload) {
  const list = read();
  list.push({
    id: newId(),
    enqueued_at: new Date().toISOString(),
    attempts: 0,
    payload,
  });
  write(list);
  return list.length;
}

export function getQueueLength() {
  return read().length;
}

export function getQueue() {
  return read();
}

export function clearQueue() {
  write([]);
}

/**
 * Try to upload every queued capture using the given async submit function.
 * `submitter(payload)` must return a promise that resolves on 2xx and
 * rejects with an `Error` carrying `.response.status` for non-2xx.
 *
 * Returns { uploaded, failed, remaining }.
 */
export async function flushQueue(submitter, { dropOn4xx = true } = {}) {
  const list = read();
  let uploaded = 0;
  let failed = 0;
  const keep = [];
  for (const entry of list) {
    try {
      await submitter(entry.payload);
      uploaded += 1;
    } catch (err) {
      const status = err?.response?.status;
      // 4xx (except 408/429) is a logical error — the same payload will
      // never succeed on retry. Drop it so the queue doesn't loop forever.
      const terminal = dropOn4xx
        && typeof status === "number"
        && status >= 400 && status < 500
        && status !== 408 && status !== 429;
      if (terminal) {
        // eslint-disable-next-line no-console
        console.warn("[attendanceQueue] dropping terminal entry", entry.id, status);
        failed += 1;
        continue;
      }
      keep.push({ ...entry, attempts: (entry.attempts || 0) + 1 });
    }
  }
  write(keep);
  return { uploaded, failed, remaining: keep.length };
}

/**
 * True when the failure looks like a network outage rather than a real
 * server-side rejection. We use this to decide whether to enqueue OR show
 * the actual error toast to the supervisor.
 */
export function isNetworkError(err) {
  if (!err) return false;
  if (err.code === "ERR_NETWORK" || err.code === "ECONNABORTED") return true;
  if (err.message && /network|timeout|fetch/i.test(err.message)) return true;
  // Axios: no response object means request never reached the server.
  if (err.request && !err.response) return true;
  return false;
}
